import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import type { PropsBase, PropsSingle } from 'react-day-picker';
import { formatLongDate } from '../../lib/date-helpers.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Text, type TextProps } from './text';

export type DatePickerProps = {
    value: Date | undefined;
    valuePrefix?: string;
    onChange: (date: Date | undefined) => void;
    disableIcon?: boolean;
    textColor?: TextProps['color'];
} & PropsSingle &
    PropsBase;

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    valuePrefix = '',
    disableIcon = false,
    className,
    textColor = 'neutral-900',
    ...props
}: DatePickerProps) => {
    const [open, setOpen] = React.useState(false);
    const { t, locale } = useLocale();

    const handleSelect = (date: Date | undefined) => {
        onChange(date);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" className={cn('justify-start text-left font-normal', className)}>
                    {!disableIcon && <CalendarIcon className="h-4 w-4 text-neutral-500" />}
                    <Text color={textColor}>
                        {value
                            ? `${valuePrefix}${formatLongDate(value, locale)}`
                            : t('placeholder__select_date')}
                    </Text>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <Calendar {...props} mode="single" selected={value} onSelect={handleSelect} />
            </PopoverContent>
        </Popover>
    );
};
